/**
 * Does a shallow comparison for props and state.
 * see: https://www.npmjs.com/package/react-addons-shallow-compare
 *
 * Note: we use a slightly different definition of equality than the original ...
 */
export function reactShallowCompare<P extends {}, S extends {}>(
  instance: React.Component<P, S>,
  nextProps: P,
  nextState: S,
): boolean {
  return !shallowEqualObjects(instance.props, nextProps) || !shallowEqualObjects(instance.state, nextState);
}

export function shallowEqualObjects<A extends {}>(a: A, b: A, excludeProp?: (key: keyof A) => boolean): boolean {
  if (a === b) {
    return true;
  }
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }
  const aProps = Object.entries(a);

  // check wether the props changed in size
  if (Object.keys(b).length !== aProps.length) {
    return false;
  }

  // check wether the props did actually change
  for (const [key, prop] of aProps) {
    if (excludeProp !== undefined && excludeProp(key) === true) {
      continue;
    }

    const nextValue = (b as any)[key];
    if (prop !== nextValue) {
      return false;
    }
  }

  return true;
}

/**
 * Shallowly compares the given arrays
 * @returns whether the given array are shallowly equal
 * @param a
 * @param b
 */
export function shallowCompareArrays(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}
